import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Search, Trash2, Eye, EyeOff, Shield, Users, TrendingUp, ShoppingCart, Package, Star, Ban, CheckCircle, FileText } from 'lucide-react';

const Admin = () => {
  const [tab, setTab] = useState('dashboard');
  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showPasswords, setShowPasswords] = useState({});

  useEffect(() => { fetchData(); }, [tab, searchTerm, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tab === 'dashboard') {
        const res = await api.get('/admin/dashboard');
        setStats(res.data);
      }
      if (tab === 'users') {
        const params = {};
        if (searchTerm) params.search = searchTerm;
        if (statusFilter) params.status = statusFilter;
        const res = await api.get('/admin/users', { params });
        setUsers(res.data);
      }
      if (tab === 'reviews') {
        const res = await api.get('/admin/reviews');
        setReviews(Array.isArray(res.data) ? res.data : []);
      }
      if (tab === 'audit') {
        const res = await api.get('/admin/audit-logs');
        setAuditLogs(Array.isArray(res.data) ? res.data : []);
      }
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const togglePassword = (id) => {
    setShowPasswords(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSuspend = async (id, name) => {
    if (!window.confirm(`Suspend ${name}?`)) return;
    try { await api.put(`/admin/users/${id}/suspend`); fetchData(); } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const handleActivate = async (id, name) => {
    if (!window.confirm(`Activate ${name}?`)) return;
    try { await api.put(`/admin/users/${id}/activate`); fetchData(); } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const handleMakeAdmin = async (id, name) => {
    if (!window.confirm(`Make ${name} an admin?`)) return;
    try { await api.put(`/admin/users/${id}/role`, { role: 'admin' }); fetchData(); } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const handleMakeUser = async (id, name) => {
    if (!window.confirm(`Remove admin rights from ${name}?`)) return;
    try { await api.put(`/admin/users/${id}/role`, { role: 'user' }); fetchData(); } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try { await api.delete(`/admin/users/${id}`); fetchData(); } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try { await api.delete(`/reviews/${id}`); fetchData(); } catch (err) {}
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2"><span className="text-2xl">🛡️</span><h2 className="text-white text-xl font-bold">Admin Panel</h2></div>

      <div className="flex gap-1 bg-[#0d1410] rounded-lg p-1 overflow-x-auto">
        {['dashboard','users','reviews','audit'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`flex-1 py-2 px-3 rounded-md text-xs font-medium capitalize whitespace-nowrap transition ${tab===t ? 'bg-red-500/20 text-red-400' : 'text-gray-400'}`}>{t}</button>
        ))}
      </div>

      {tab === 'dashboard' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            { label: 'Total Users', value: stats.totalUsers || 0, icon: '👥' },
            { label: 'Active', value: stats.activeUsers || 0, icon: '✅' },
            { label: 'Suspended', value: stats.suspendedUsers || 0, icon: '🚫' },
            { label: 'Admins', value: stats.adminUsers || 0, icon: '🛡️' },
            { label: 'Sales', value: stats.totalSales || 0, icon: '🛒' },
            { label: 'Products', value: stats.totalProducts || 0, icon: '📦' },
            { label: 'Purchases', value: stats.totalPurchases || 0, icon: '📈' },
            { label: 'Expenses', value: stats.totalExpenses || 0, icon: '📋' },
          ].map(s => (
            <div key={s.label} className="bg-[#0d1410] border border-white/5 rounded-xl p-3 text-center">
              <div className="text-xl mb-1">{s.icon}</div>
              <p className="text-white text-xl font-bold">{s.value}</p>
              <p className="text-gray-500 text-[10px] uppercase">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'users' && (
        <div>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-2.5 text-gray-500" />
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search users..." className="w-full pl-9 pr-4 py-2.5 bg-[#0d1410] border border-white/5 rounded-lg text-white text-sm" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2.5 bg-[#0d1410] border border-white/5 rounded-lg text-white text-sm">
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>

          <div className="bg-[#0d1410] border border-white/5 rounded-xl overflow-hidden">
            {users.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-8">No users found</p>
            ) : (
              users.map(u => (
                <div key={u.id} className="p-3 border-b border-white/5">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white text-sm font-medium">{u.fullName}</p>
                        {u.role === 'admin' && <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 text-[10px] rounded">ADMIN</span>}
                        {u.accountStatus === 'suspended' && <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-[10px] rounded">SUSPENDED</span>}
                      </div>
                      <p className="text-gray-500 text-xs">{u.phone} • {u.shopName}</p>
                      <p className="text-gray-500 text-[10px]">{u.totalSales || 0} sales</p>
                    </div>
                    <div className="flex gap-1">
                      {u.accountStatus === 'active' ? (
                        <button onClick={() => handleSuspend(u.id, u.fullName)} className="p-1.5 text-yellow-500 hover:bg-yellow-500/10 rounded" title="Suspend"><Ban size={14} /></button>
                      ) : (
                        <button onClick={() => handleActivate(u.id, u.fullName)} className="p-1.5 text-emerald-400 hover:bg-emerald-500/10 rounded" title="Activate"><CheckCircle size={14} /></button>
                      )}
                      {u.role === 'admin' ? (
                        <button onClick={() => handleMakeUser(u.id, u.fullName)} className="p-1.5 text-gray-400 hover:bg-gray-500/10 rounded" title="Remove admin"><Shield size={14} /></button>
                      ) : (
                        <button onClick={() => handleMakeAdmin(u.id, u.fullName)} className="p-1.5 text-blue-400 hover:bg-blue-500/10 rounded" title="Make admin"><Shield size={14} /></button>
                      )}
                      <button onClick={() => handleDelete(u.id)} className="p-1.5 text-red-400 hover:bg-red-500/10 rounded" title="Delete"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === 'reviews' && (
        <div className="space-y-2">
          {reviews.length === 0 ? (
            <div className="bg-[#0d1410] border border-white/5 rounded-xl p-8 text-center"><p className="text-gray-500">No reviews yet</p></div>
          ) : (
            reviews.map(r => (
              <div key={r.id} className="bg-[#0d1410] border border-white/5 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1"><span className="text-white text-sm font-medium">{r.name}</span><span className="text-yellow-400">{'★'.repeat(r.rating)}</span></div>
                <p className="text-gray-400 text-sm">{r.message}</p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-gray-600 text-[10px]">{r.createdAt?.split('T')[0]}</p>
                  <button onClick={() => handleDeleteReview(r.id)} className="text-gray-600 hover:text-red-400"><Trash2 size={12} /></button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === 'audit' && (
        <div className="bg-[#0d1410] border border-white/5 rounded-xl overflow-hidden">
          <div className="p-3 border-b border-white/5 flex items-center gap-2">
            <FileText size={14} className="text-gray-400" />
            <h3 className="text-white text-sm font-semibold">Recent Activity (Last 100)</h3>
          </div>
          {auditLogs.length === 0 ? (
            <p className="text-gray-500 text-sm text-center py-8">No logs yet</p>
          ) : (
            auditLogs.map(log => (
              <div key={log.id} className="p-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    log.action?.includes('FAILED') ? 'bg-red-500/20 text-red-400' :
                    log.action?.includes('BLOCKED') || log.action?.includes('SUSPEND') ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-emerald-500/20 text-emerald-400'
                  }`}>{log.action}</span>
                  <span className="text-white text-sm">{log.userName || 'Unknown'}</span>
                </div>
                <p className="text-gray-400 text-xs mt-1">{log.details}</p>
                <p className="text-gray-600 text-[10px] mt-0.5">{log.createdAt?.replace('T', ' ')}</p>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default Admin;
